import { Command } from 'commander';
import { VaultProjectContextService } from '../../services/vault-project-context.service.js';
import type { VaultPlainItem } from '../../types/vault.types.js';
import { redactVaultItem, unlockVault } from './shared.js';

interface ListOptions {
    tag?: string;
    project?: boolean;
    passphrase?: string;
    recoveryKey?: string;
    json?: boolean;
}

function truncate(value: string, maxLength: number): string {
    if (value.length <= maxLength) return value;
    if (maxLength <= 3) return value.slice(0, maxLength);
    return `${value.slice(0, maxLength - 3)}...`;
}

function pad(value: string, width: number): string {
    return value.padEnd(width, ' ');
}

export function formatVaultList(items: VaultPlainItem[]): string {
    const rows = items.map((item) => ({
        id: item.id,
        type: item.type,
        sensitivity: item.sensitivity,
        secret: item.containsSecret ? 'yes' : 'no',
        title: truncate(item.title, 32),
        tags: truncate(item.tags.join(', ') || '-', 36),
    }));
    const widths = {
        id: Math.max('ID'.length, ...rows.map((row) => row.id.length)),
        type: Math.max('TYPE'.length, ...rows.map((row) => row.type.length)),
        sensitivity: Math.max('SENS'.length, ...rows.map((row) => row.sensitivity.length)),
        secret: Math.max('SECRET'.length, ...rows.map((row) => row.secret.length)),
        title: Math.max('TITLE'.length, ...rows.map((row) => row.title.length)),
    };
    const header = [
        pad('ID', widths.id),
        pad('TYPE', widths.type),
        pad('SENS', widths.sensitivity),
        pad('SECRET', widths.secret),
        pad('TITLE', widths.title),
        'TAGS',
    ].join('  ');
    const divider = [
        '-'.repeat(widths.id),
        '-'.repeat(widths.type),
        '-'.repeat(widths.sensitivity),
        '-'.repeat(widths.secret),
        '-'.repeat(widths.title),
        '----',
    ].join('  ');
    const body = rows.map((row) => [
        pad(row.id, widths.id),
        pad(row.type, widths.type),
        pad(row.sensitivity, widths.sensitivity),
        pad(row.secret, widths.secret),
        pad(row.title, widths.title),
        row.tags,
    ].join('  '));

    return [header, divider, ...body].join('\n');
}

export function createVaultListCommand(): Command {
    return new Command('list')
        .description('List encrypted vault items after local decrypt')
        .option('--tag <tag>', 'Filter by tag')
        .option('--project', 'Filter by detected Jai1 project tags')
        .option('--passphrase <value>', 'Vault passphrase')
        .option('--recovery-key <value>', 'Recovery key')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, options: ListOptions) {
            const { store, vmk } = await unlockVault(options);
            let items = await store.listPlainItems(vmk);
            if (options.tag) items = items.filter((item) => item.tags.includes(options.tag!));
            if (options.project) items = new VaultProjectContextService().filterToCurrentProject(items);
            const useJson = !!(options.json || this.optsWithGlobals().json);
            if (useJson) {
                console.log(JSON.stringify({ items: items.map(redactVaultItem) }, null, 2));
                return;
            }
            if (items.length === 0) {
                console.log('No vault items found.');
                return;
            }
            console.log(formatVaultList(items));
        });
}
